'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface TrustSignalsSectionProps {
  className?: string;
}

const TrustSignalsSection = ({ className = '' }: TrustSignalsSectionProps) => {
  return (
    <section className={`relative overflow-hidden py-20 lg:py-32 bg-[radial-gradient(circle_at_top_left,rgba(44,120,251,0.08),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(62,224,190,0.12),transparent_30%),linear-gradient(180deg,#f7fbff_0%,#ffffff_48%,#eefcf8_100%)] ${className}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Token System Explanation */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="text-center mb-12">
            <div className="section-eyebrow mb-6">
              <Icon name="BoltIcon" size={18} className="text-[var(--primary)]" />
              <span>Sistema de Tokens</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
              ¿Cómo Funciona el{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>
                Sistema de Tokens?
              </span>
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              Un modelo de pago justo y transparente. Solo pagas por las funciones que realmente usas.
            </p>
          </div>

          <div className="app-card rounded-3xl p-8 lg:p-12 border border-[rgba(44,120,251,0.12)]">
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundImage: 'linear-gradient(135deg, #2c78fb 0%, #58a6ff 100%)' }}>
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Regístrate Gratis</h3>
                    <p className="text-gray-600">
                      Crea tu cuenta sin costo y recibe 40 tokens iniciales para empezar a explorar.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundImage: 'linear-gradient(135deg, #0f3f87 0%, #2c78fb 100%)' }}>
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Usa tus Tokens</h3>
                    <p className="text-gray-600">
                      Cada función consume una cantidad específica de tokens. Puedes combinar acciones rápidas o análisis más completos según lo que necesites.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundImage: 'linear-gradient(135deg, #3ee0be 0%, #0f8f7b 100%)' }}>
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Recarga Semanal</h3>
                    <p className="text-gray-600">
                      Los lunes puedes recibir una recarga automática según tu plan, solo si tu saldo está en 5 tokens o menos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl p-8 border border-[rgba(44,120,251,0.14)] bg-[linear-gradient(180deg,rgba(44,120,251,0.08)_0%,rgba(62,224,190,0.08)_100%)]">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Ejemplo de Costos</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-[rgba(44,120,251,0.16)]">
                    <div className="flex items-center space-x-3">
                      <Icon name="ChatBubbleLeftIcon" size={20} className="text-[var(--primary)]" />
                      <span className="text-gray-700 font-medium">Diagnóstico AI Coach</span>
                    </div>
                    <span className="text-[var(--primary)] font-bold">10 tokens</span>
                  </div>

                  <div className="flex items-center justify-between pb-3 border-b border-[rgba(44,120,251,0.16)]">
                    <div className="flex items-center space-x-3">
                      <Icon name="BoltIcon" size={20} className="text-sky-700" />
                      <span className="text-gray-700 font-medium">Acción rápida</span>
                    </div>
                    <span className="text-sky-700 font-bold">2 tokens</span>
                  </div>

                  <div className="flex items-center justify-between pb-3 border-b border-[rgba(44,120,251,0.16)]">
                    <div className="flex items-center space-x-3">
                      <Icon name="ChartBarIcon" size={20} className="text-emerald-700" />
                      <span className="text-gray-700 font-medium">Análisis de competencia</span>
                    </div>
                    <span className="text-emerald-700 font-bold">3 tokens</span>
                  </div>

                  <div className="flex items-center justify-between pb-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="DocumentTextIcon" size={20} className="text-[var(--primary-strong)]" />
                      <span className="text-gray-700 font-medium">Plan de mejora</span>
                    </div>
                    <span className="text-[var(--primary-strong)] font-bold">4 tokens</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white/80 rounded-xl border border-[rgba(44,120,251,0.16)]">
                  <p className="text-sm text-gray-600 text-center">
                    💡 Usa cualquiera de nuestros mentores con tus tokens, sin restricciones por plan.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-2xl border border-[rgba(44,120,251,0.14)] bg-white/75">
                <Icon name="BoltIcon" size={32} className="text-[var(--primary)] mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 mb-2">Sin Vencimiento</h4>
                <p className="text-sm text-gray-600">Tus tokens nunca expiran, úsalos cuando lo necesites</p>
              </div>

              <div className="text-center p-6 rounded-2xl border border-[rgba(44,120,251,0.14)] bg-white/75">
                <Icon name="ArrowPathIcon" size={32} className="text-sky-700 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 mb-2">Recarga con Umbral</h4>
                <p className="text-sm text-gray-600">La recarga llega los lunes solo si tu saldo esta en 5 o menos</p>
              </div>

              <div className="text-center p-6 rounded-2xl border border-[rgba(62,224,190,0.22)] bg-white/75">
                <Icon name="CheckCircleIcon" size={32} className="text-[var(--accent)] mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 mb-2">Paga lo que Uses</h4>
                <p className="text-sm text-gray-600">Solo consumes tokens cuando usas funciones premium</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="hidden lg:block text-center">
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12">
            Confía en una Plataforma Diseñada para Ti
          </h3>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-black text-transparent bg-clip-text mb-3" style={{ backgroundImage: 'var(--gradient-app)' }}>
                24/7
              </div>
              <p className="text-gray-600 font-medium">Disponibilidad</p>
            </div>

            <div className="text-center">
              <div className="text-5xl font-black text-transparent bg-clip-text mb-3" style={{ backgroundImage: 'linear-gradient(135deg, #0f3f87 0%, #2c78fb 100%)' }}>
                &lt;5min
              </div>
              <p className="text-gray-600 font-medium">Tiempo de Análisis</p>
            </div>

            <div className="text-center">
              <div className="text-5xl font-black text-transparent bg-clip-text mb-3" style={{ backgroundImage: 'linear-gradient(135deg, #2c78fb 0%, #3ee0be 100%)' }}>
                100%
              </div>
              <p className="text-gray-600 font-medium">Personalizado</p>
            </div>

            <div className="text-center">
              <div className="text-5xl font-black text-transparent bg-clip-text mb-3" style={{ backgroundImage: 'linear-gradient(135deg, #0f3f87 0%, #3ee0be 100%)' }}>
                🇲🇽
              </div>
              <p className="text-gray-600 font-medium">Para Latinoamérica</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSignalsSection;
